import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { useTranslation } from "react-i18next";
import Logo from "../../../../assets/images/logo.svg";

const AboutSection: React.FC = () => {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Set initial state
    gsap.set([titleRef.current, descriptionRef.current, logoRef.current], {
      opacity: 0,
      y: 50,
    });

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 80%", // Start animation when top of section is 80% from top of viewport
          end: "bottom 20%", // End animation when bottom of section is 20% from top of viewport
          toggleActions: "play none none reverse", // Play on enter, reverse on leave
          markers: false, // Set to true for debugging
        },
        defaults: {
          ease: "power2.out",
          willReadFrequently: false,
        },
      });

      tl.to(titleRef.current, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        clearProps: "willChange",
      })
        .to(
          descriptionRef.current,
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            clearProps: "willChange",
          },
          "-=0.3",
        )
        .to(
          logoRef.current,
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            clearProps: "willChange",
          },
          "-=0.3",
        );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={containerRef}
      id="about-section"
      className="flex flex-col items-center justify-center px-6 md:px-20 lg:px-[20%] xl:px-[30%] py-5 mb-[4vh] lg:py-[10vh]"
    >
      <h1
        ref={titleRef}
        className="text-[24px] font-[400] text-center mb-8 md:text-[32px] lg:text-[40px] font-title italic"
      >
        {t("about.title")}
      </h1>
      <p
        ref={descriptionRef}
        className="text-[14kjpx] text-center max-w-3xl md:text-[16px] lg:text-[18px]"
      >
        {t("about.description")}
      </p>
      <div className="relative mt-16" ref={logoRef}>
        <img
          className="absolute top-[2%] left-[2%] w-[26%] invert brightness-0"
          src={Logo}
          alt="logo"
        />
        <img
          className="w-full object-contain rounded-md"
          src={require("../../../../assets/images/lots.webp")}
          width="auto"
          height="auto"
          alt="land"
        />
      </div>
    </div>
  );
};

export default AboutSection;
